/**
 * Browser-side proctoring: detects suspicious activity during an exam.
 * - Tab switch (visibilitychange)
 * - Window blur
 * - Fullscreen exit
 * - Copy/paste attempts
 * - Webcam face detection via face-api.js (tiny model, loads on demand)
 *
 * Sends violations to /api/attempts/violations.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Eye, Warning, VideoCameraSlash, Microphone, MicrophoneSlash, Monitor, MonitorPlay } from "@phosphor-icons/react";

const FACEAPI_MODEL_URL = "https://justadudewhohacks.github.io/face-api.js/models";

export default function ProctoringWatcher({ attemptId, enabled = true, hidden = false }) {
  const [violations, setViolations] = useState([]);
  const [score, setScore] = useState(0);
  const [suspended, setSuspended] = useState(false);
  const [cameraOk, setCameraOk] = useState(false);
  const [faceStatus, setFaceStatus] = useState("loading"); // loading | ok | no_face | multi_face | error
  const [micLevel, setMicLevel] = useState(0);
  const [micOn, setMicOn] = useState(false);
  const [screenOn, setScreenOn] = useState(false);
  const videoRef = useRef(null);
  const faceapiRef = useRef(null);
  const intervalRef = useRef(null);
  const micStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const audioCtxRef = useRef(null);

  const reportViolation = async (type, severity = "low", detail = "") => {
    try {
      const r = await api.post("/attempts/violations", { attempt_id: attemptId, type, severity, detail });
      setScore(r.data.violation_score);
      setSuspended(r.data.auto_suspended);
      setViolations((v) => [{ type, severity, at: new Date().toISOString(), detail }, ...v].slice(0, 30));
    } catch {}
  };

  // Event-based detectors
  useEffect(() => {
    if (!enabled || !attemptId) return;

    const onVisibility = () => {
      if (document.hidden) reportViolation("tab_switch", "medium", "Tab/window hidden");
    };
    const onBlur = () => reportViolation("blur", "low", "Window lost focus");
    const onFsChange = () => {
      if (!document.fullscreenElement) reportViolation("fullscreen_exit", "medium", "Exited fullscreen");
    };
    const onCopy = (e) => { e.preventDefault(); reportViolation("copy", "high", "Copy blocked"); };
    const onPaste = (e) => { e.preventDefault(); reportViolation("paste", "high", "Paste blocked"); };
    const onCtx = (e) => e.preventDefault();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("copy", onCopy);
    document.addEventListener("paste", onPaste);
    document.addEventListener("contextmenu", onCtx);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("paste", onPaste);
      document.removeEventListener("contextmenu", onCtx);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, enabled]);

  // Webcam + face detection
  useEffect(() => {
    if (!enabled || !attemptId) return;
    let stream;
    let cancelled = false;

    (async () => {
      try {
        const faceapi = await import("face-api.js");
        faceapiRef.current = faceapi;
        await faceapi.nets.tinyFaceDetector.loadFromUri(FACEAPI_MODEL_URL);
        if (cancelled) return;

        stream = await navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 }, audio: false });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        // Mirror the stream to the inline exam camera panel if present
        try {
          const mirror = window.__examCameraTarget;
          if (mirror) {
            mirror.srcObject = stream;
            mirror.play().catch(() => {});
          }
        } catch {}
        setCameraOk(true);
        setFaceStatus("ok");

        // Frame upload canvas
        const canvas = document.createElement("canvas");
        canvas.width = 320;
        canvas.height = 240;
        const ctx = canvas.getContext("2d");
        let currentStatus = "ok";

        const uploadFrame = async () => {
          try {
            if (!videoRef.current || videoRef.current.readyState < 2) return;
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL("image/jpeg", 0.55);
            await api.post(`/attempts/${attemptId}/frame`, { image_b64: dataUrl, face_status: currentStatus });
          } catch {}
        };
        // upload first frame quickly then every 4s
        setTimeout(uploadFrame, 800);
        const uploadTimer = setInterval(uploadFrame, 4000);

        intervalRef.current = setInterval(async () => {
          if (!videoRef.current) return;
          try {
            const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 });
            const detections = await faceapi.detectAllFaces(videoRef.current, opts);
            if (detections.length === 0) {
              setFaceStatus("no_face");
              currentStatus = "no_face";
              reportViolation("no_face", "medium", "No face detected");
            } else if (detections.length > 1) {
              setFaceStatus("multi_face");
              currentStatus = "multi_face";
              reportViolation("multi_face", "high", `${detections.length} faces`);
            } else {
              setFaceStatus("ok");
              currentStatus = "ok";
            }
          } catch {}
        }, 5000);

        // Attach cleanup for uploadTimer via closure
        const prevCleanup = intervalRef.current;
        intervalRef.current = {
          _face: prevCleanup,
          _upload: uploadTimer,
        };
      } catch (e) {
        setCameraOk(false);
        setFaceStatus("error");
        reportViolation("camera_denied", "high", String(e?.message || e));
      }
    })();

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        if (intervalRef.current._face) clearInterval(intervalRef.current._face);
        if (intervalRef.current._upload) clearInterval(intervalRef.current._upload);
        if (typeof intervalRef.current === "number") clearInterval(intervalRef.current);
      }
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, enabled]);

  // Microphone monitoring (RMS)
  useEffect(() => {
    if (!enabled || !attemptId) return;
    let cancelled = false;
    let raf;
    let voiceStreakStart = 0;
    const start = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        micStreamRef.current = s;
        setMicOn(true);
        const AC = window.AudioContext || window.webkitAudioContext;
        const ac = new AC();
        audioCtxRef.current = ac;
        const src = ac.createMediaStreamSource(s);
        const analyser = ac.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (cancelled) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
          const rms = Math.sqrt(sum / buf.length);
          const level = Math.min(100, Math.round(rms * 300));
          setMicLevel(level);
          const now = Date.now();
          if (level > 25) {
            if (!voiceStreakStart) voiceStreakStart = now;
            else if (now - voiceStreakStart > 3500) {
              // Sustained talking for 3.5s
              voiceStreakStart = now + 5000; // debounce next report
              reportViolation("talking_detected", "medium", `Sustained voice (level ${level})`);
            }
          } else if (level < 12) {
            voiceStreakStart = 0;
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e) {
        setMicOn(false);
        reportViolation("mic_denied", "medium", String(e?.message || e));
      }
    };
    start();
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      if (micStreamRef.current) micStreamRef.current.getTracks().forEach((t) => t.stop());
      if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch {} }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId, enabled]);

  // Screen share (recording indicator) — student must share screen; if they stop, we flag.
  const requestScreenShare = async () => {
    try {
      const s = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 5 }, audio: false });
      screenStreamRef.current = s;
      setScreenOn(true);
      const track = s.getVideoTracks()[0];
      if (track) {
        track.addEventListener("ended", () => {
          setScreenOn(false);
          reportViolation("screen_share_stopped", "high", "Student ended screen share");
        });
      }
    } catch (e) {
      setScreenOn(false);
      reportViolation("screen_share_denied", "medium", String(e?.message || e));
    }
  };
  useEffect(() => {
    return () => {
      if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  if (!enabled) return null;

  const statusColor = {
    ok: "bg-green-500",
    no_face: "bg-amber-500",
    multi_face: "bg-red-500",
    error: "bg-zinc-400",
    loading: "bg-zinc-400",
  }[faceStatus];

  return (
    <div className={`fixed bottom-4 left-4 w-64 border border-zinc-900 bg-white z-50 shadow-2xl ${hidden ? "hidden" : ""}`} data-testid="proctor-watcher">
      <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-900 text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye size={14} weight="fill" />
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">Proctoring</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 ${statusColor} pulse-dot`} />
          <span className="text-[10px] font-mono">{faceStatus.toUpperCase()}</span>
        </div>
      </div>

      <div className="relative bg-zinc-100">
        <video ref={videoRef} muted autoPlay playsInline className="w-full h-32 object-cover" />
        {!cameraOk && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-600">
            <VideoCameraSlash size={24} />
            <div className="text-[10px] uppercase tracking-[0.2em] mt-1">Camera off</div>
          </div>
        )}
      </div>

      {/* Mic level meter */}
      <div className="px-3 py-2 border-t border-zinc-200 flex items-center gap-2" data-testid="mic-meter">
        {micOn ? <Microphone size={12} className="text-zinc-700" /> : <MicrophoneSlash size={12} className="text-zinc-400" />}
        <div className="flex-1 h-1.5 bg-zinc-200 relative overflow-hidden">
          <div
            className={`h-full transition-all ${micLevel > 60 ? "bg-red-500" : micLevel > 25 ? "bg-amber-500" : "bg-green-500"}`}
            style={{ width: `${micLevel}%` }}
          />
        </div>
        <span className="text-[9px] font-mono w-6 text-right text-zinc-500">{micLevel}</span>
      </div>

      {/* Screen share */}
      <div className="px-3 py-2 border-t border-zinc-200 flex items-center gap-2" data-testid="screen-share-row">
        {screenOn ? <MonitorPlay size={12} weight="fill" className="text-green-600" /> : <Monitor size={12} className="text-zinc-400" />}
        <div className="flex-1 text-[10px] uppercase tracking-[0.15em] text-zinc-600">
          Screen: {screenOn ? <span className="text-green-700 font-semibold">Sharing</span> : <span className="text-zinc-500">Not shared</span>}
        </div>
        {!screenOn && (
          <button
            onClick={requestScreenShare}
            className="text-[10px] uppercase tracking-wider border border-zinc-900 px-2 py-0.5 hover:bg-zinc-900 hover:text-white"
            data-testid="screen-share-btn"
          >
            Share
          </button>
        )}
      </div>

      <div className="px-3 py-2 border-t border-zinc-200 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Violation score</div>
          <div className={`font-mono-tab font-bold text-lg ${score > 50 ? "text-red-600" : score > 25 ? "text-amber-600" : "text-zinc-900"}`} data-testid="violation-score">
            {score}
          </div>
        </div>
        {suspended && (
          <div className="border border-red-600 bg-red-50 text-red-700 text-[10px] uppercase tracking-[0.2em] px-2 py-1 font-semibold">
            <Warning size={12} weight="fill" className="inline" /> Flagged
          </div>
        )}
      </div>

      {violations.length > 0 && (
        <div className="border-t border-zinc-200 max-h-24 overflow-y-auto">
          {violations.slice(0, 5).map((v, i) => (
            <div key={i} className="px-3 py-1.5 text-[10px] border-b border-zinc-100 last:border-b-0 flex items-center justify-between">
              <span className="font-medium truncate">{v.type.replace(/_/g, " ")}</span>
              <span className={`uppercase tracking-wider ${v.severity === "high" ? "text-red-600" : v.severity === "medium" ? "text-amber-600" : "text-zinc-500"}`}>{v.severity}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
